import { formatClockParts, formatHourMark, getTimeFormat } from '@/shared/lib/time-format';
import { useEffect, useMemo } from 'react';
import { presenceShiftLabel, type DoctorPresence } from '@/scheduling/services/use-doctor-presence';
import { useActorShifts } from '@/scheduling/services/use-actor-shifts';
import {
  BLOCK_ACTOR_PRACTITIONER,
  type BlockActorKind,
} from '@/types/block-availability-api';

/**
 * BlockAvailabilityBreakForm — Flutter parity with `break_widget.dart`
 * (lines 136-619): REASON + SHIFTS dropdown + Start/End hour+minute
 * dropdowns, all bounded by the selected shift's working hours.
 *
 * Time picker shape: matches Flutter's two-dropdown pattern
 * (`break_widget.dart:363-516` start, `:527-619` end):
 *   - Hour dropdown: every hour in `[shift.start ... shift.end]`.
 *     Start excludes the closing hour (`break_widget.dart:85-94`
 *     `startHrs()`); End includes it (`:96-102` `endHrs()`).
 *   - Minute dropdown: 0, 5, 10, ..., 55 (5-min step).
 *   - End hour must be >= Start hour; End minute > Start minute when
 *     same hour (Flutter `:501-505`).
 *
 * Native `<select>` chosen over a custom Combobox: simpler, native
 * a11y / keyboard, matches the SHIFTS dropdown style above so the
 * three controls visually align. The min/max constraints are
 * enforced by filtering the option list (not just `disabled` flags
 * on items) so the user genuinely can't pick an invalid combo.
 *
 * ACTOR-GENERIC since PROD-2454. The only practitioner-specific thing this
 * form ever had was the call to `/api/Doctor/Presence`; everything else —
 * the SHIFTS dropdown, the bounded hour lists, the end-after-start rule, the
 * reason field — reasons about a working window and a pair of times, which a
 * machine has exactly as much as a doctor does. That one call now goes
 * through `useActorShifts`, which picks the feed from `actorKind`, and the
 * rest of the file is untouched. The label still reads "Shifts" for a
 * device: it IS the same idea (the window the actor is available in), and
 * inventing a second word for it on one tab would be the drift this repo
 * keeps extracting helpers to avoid.
 */

interface Props {
  /**
   * The actor being blocked — a practitioner GUID or a device GUID
   * (PROD-2454). The form itself is indifferent: everything below this line
   * reasons about a working window and a pair of times, and always did. What
   * used to be practitioner-specific was only WHERE the window came from,
   * and that now lives behind `useActorShifts`.
   */
  actorId: string;
  /** Which feed the shift list comes from. See `useActorShifts`. */
  actorKind?: BlockActorKind;
  /** Practice GUID — required by the device shift feed; unused for a practitioner. */
  practiceId: string;
  blockDate: Date;
  shiftKey: string | null;
  onChangeShift: (key: string | null, shift: DoctorPresence | null) => void;
  /** HH:MM 24h string. */
  startTime: string | null;
  /** HH:MM 24h string. */
  endTime: string | null;
  comment: string;
  onChangeStartTime: (t: string | null) => void;
  onChangeEndTime: (t: string | null) => void;
  onChangeComment: (c: string) => void;
  /** When false, all fields disable (PR 5 edit mode). */
  disabled?: boolean;
  /**
   * View-only mode (PR 5 edit panel). Mirrors Flutter
   * `break_widget.dart:160-217` (display-only Start/End text fields)
   * and the `!widget.isEdit!` gate at `:252, :365, :535` that hides
   * the SHIFTS dropdown + hour/minute pickers in edit mode.
   *
   *  - SHIFTS dropdown: hidden.
   *  - Start/End: rendered as disabled text inputs showing the
   *    formatted time (e.g. "02:00 PM") instead of hour+minute
   *    dropdowns. Hour/minute dropdowns depend on a selected shift
   *    to populate their options — useless in view mode.
   *  - REASON: still a text input but `disabled` (via the prop).
   */
  viewOnly?: boolean;
}

const LABEL_CLS =
  'block text-xs font-medium text-text-muted mb-1';
const SELECT_CLS =
  'w-full h-10 px-2 rounded-md border border-[var(--surface-line)] bg-[var(--surface-read)] [font:var(--type-body)] text-[var(--ink-body)] focus:border-brand-primary focus:outline-none disabled:bg-grey-100 disabled:text-text-muted';
const INPUT_CLS =
  'w-full h-10 px-3 rounded-md border border-[var(--surface-line)] bg-[var(--surface-read)] [font:var(--type-body)] text-[var(--ink-body)] focus:border-brand-primary focus:outline-none disabled:bg-grey-100 disabled:text-text-muted';

const MINUTE_OPTIONS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

function shiftToKey(p: DoctorPresence): string {
  return p.id ?? p.start ?? '';
}

/**
 * Label for an hour `0-23`: "08:00" / "20:00" (24-hour). Under the 12-hour
 * preference `8` → "8 AM", `20` → "8 PM", `0` → "12 AM", `12` → "12 PM",
 * Flutter's `DateFormat("hh a")` output without the leading zero
 * (the dropdown is narrower without it).
 */
function hourLabel(h: number): string {
  if (getTimeFormat() === '24h') return formatHourMark(h, '24h');
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12} ${ampm}`;
}

/** Extract the UTC hour from a `Z`-stamped wall-clock ISO. */
function utcHour(iso: string | null): number | null {
  if (!iso) return null;
  return new Date(iso).getUTCHours();
}

/** Parse `HH:MM` into [hour, minute]. */
function parseHHMM(hhmm: string | null): { hour: number | null; minute: number | null } {
  if (!hhmm) return { hour: null, minute: null };
  const [h, m] = hhmm.split(':').map(Number);
  return { hour: Number.isFinite(h) ? h : null, minute: Number.isFinite(m) ? m : null };
}

/** Compose `HH:MM` from numeric hour + minute (each padded). */
function toHHMM(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/**
 * Display format for view-only mode: `"14:00"` (24-hour), or under the
 * 12-hour preference Flutter's `scTimeFormatLetter` rendering at
 * `break_widget.dart:182, 203`: `"02:00 PM"` / `"03:00 PM"`. Returns `''`
 * for an unset time so the input shows a placeholder.
 */
function formatTimeForDisplay(hhmm: string | null): string {
  if (!hhmm) return '';
  const [hStr, mStr] = hhmm.split(':');
  const h = Number(hStr);
  const m = Number(mStr) || 0;
  if (getTimeFormat() === '24h') return formatClockParts(h, m, '24h');
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
}

export function BlockAvailabilityBreakForm({
  actorId,
  actorKind = BLOCK_ACTOR_PRACTITIONER,
  practiceId,
  blockDate,
  shiftKey,
  onChangeShift,
  startTime,
  endTime,
  comment,
  onChangeStartTime,
  onChangeEndTime,
  onChangeComment,
  disabled = false,
  viewOnly = false,
}: Props) {
  const { shifts, isLoading: shiftsLoading } = useActorShifts({
    kind: actorKind,
    actorId,
    practiceId,
    date: blockDate,
  });
  const selectedShift = shifts.find((s) => shiftToKey(s) === shiftKey) ?? null;

  // Auto-select sole shift; reset when no longer present.
  useEffect(() => {
    if (!shiftKey && shifts.length === 1) {
      onChangeShift(shiftToKey(shifts[0]), shifts[0]);
    }
    if (shiftKey && shifts.length > 0 && !shifts.find((s) => shiftToKey(s) === shiftKey)) {
      onChangeShift(null, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shifts, shiftKey]);

  const shiftStartHour = utcHour(selectedShift?.start ?? null);
  const shiftEndHour = utcHour(selectedShift?.end ?? null);

  // Hour option lists — Flutter parity:
  //   startHrs() : [shift.start ... shift.end - 1]   (no closing hour)
  //   endHrs()   : [shift.start ... shift.end]       (closing hour ok)
  const { startHours, endHours } = useMemo(() => {
    if (shiftStartHour === null || shiftEndHour === null || shiftEndHour <= shiftStartHour) {
      return { startHours: [] as number[], endHours: [] as number[] };
    }
    const start: number[] = [];
    const end: number[] = [];
    for (let h = shiftStartHour; h <= shiftEndHour; h++) {
      if (h < shiftEndHour) start.push(h);
      end.push(h);
    }
    return { startHours: start, endHours: end };
  }, [shiftStartHour, shiftEndHour]);

  const startParts = parseHHMM(startTime);
  const endParts = parseHHMM(endTime);

  // Filter End hours: must be >= Start hour.
  const filteredEndHours = useMemo(() => {
    if (startParts.hour === null) return endHours;
    return endHours.filter((h) => h >= startParts.hour!);
  }, [endHours, startParts.hour]);

  // Filter End minutes: when same hour as Start, must be > Start minute.
  const filteredEndMinutes = useMemo(() => {
    if (
      startParts.hour !== null &&
      endParts.hour !== null &&
      startParts.hour === endParts.hour &&
      startParts.minute !== null
    ) {
      return MINUTE_OPTIONS.filter((m) => m > startParts.minute!);
    }
    return MINUTE_OPTIONS;
  }, [startParts.hour, startParts.minute, endParts.hour]);

  const timePickerEnabled = !disabled && !!selectedShift && startHours.length > 0;

  const updateStart = (nextHour: number | null, nextMinute: number | null) => {
    if (nextHour === null || nextMinute === null) {
      onChangeStartTime(null);
      return;
    }
    onChangeStartTime(toHHMM(nextHour, nextMinute));
    // If End is now invalid (end <= start), clear it so the user re-picks.
    if (endParts.hour !== null && endParts.minute !== null) {
      const startMin = nextHour * 60 + nextMinute;
      const endMin = endParts.hour * 60 + endParts.minute;
      if (endMin <= startMin) onChangeEndTime(null);
    }
  };

  const updateEnd = (nextHour: number | null, nextMinute: number | null) => {
    if (nextHour === null || nextMinute === null) {
      onChangeEndTime(null);
      return;
    }
    onChangeEndTime(toHHMM(nextHour, nextMinute));
  };

  return (
    <div className="space-y-4">
      <div>
        <label className={LABEL_CLS}>
          Reason <span className="text-[var(--status-bad)]">*</span>
        </label>
        <input
          type="text"
          placeholder="Enter reason"
          value={comment}
          onChange={(e) => onChangeComment(e.target.value)}
          disabled={disabled}
          className={INPUT_CLS}
          maxLength={200}
        />
      </div>

      {/* SHIFTS — Flutter `break_widget.dart:255-355`. Hidden in
          view-only mode (`!widget.isEdit!` gate at `:252`). */}
      {!viewOnly && (
      <div>
        <label className={LABEL_CLS}>Shifts</label>
        {shiftsLoading ? (
          <div className="h-10 rounded-md bg-grey-100 animate-pulse" />
        ) : shifts.length === 0 ? (
          <p className="[font:var(--type-body)] text-[var(--ink-meta)] py-1">No Slots Available</p>
        ) : (
          <select
            value={shiftKey ?? ''}
            onChange={(e) => {
              const next = e.target.value;
              const match = shifts.find((s) => shiftToKey(s) === next) ?? null;
              onChangeShift(next || null, match);
              onChangeStartTime(null);
              onChangeEndTime(null);
            }}
            disabled={disabled}
            className={SELECT_CLS}
          >
            <option value="">Select a shift</option>
            {shifts.map((s) => (
              <option key={shiftToKey(s)} value={shiftToKey(s)}>
                {presenceShiftLabel(s)}
              </option>
            ))}
          </select>
        )}
      </div>
      )}

      {/* View-only Start/End — disabled text inputs showing the
          formatted saved time. Flutter `break_widget.dart:160-217`
          (`widget.isEdit!` branch). */}
      {viewOnly && (
        <div className="space-y-3">
          <div>
            <label className={LABEL_CLS}>
              Start time <span className="text-[var(--status-bad)]">*</span>
            </label>
            <input
              type="text"
              value={formatTimeForDisplay(startTime)}
              disabled
              placeholder="Start Time"
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>
              End time <span className="text-[var(--status-bad)]">*</span>
            </label>
            <input
              type="text"
              value={formatTimeForDisplay(endTime)}
              disabled
              placeholder="End Time"
              className={INPUT_CLS}
            />
          </div>
        </div>
      )}

      {/* Start / End time — hour + minute dropdowns, bounded by shift.
          Hidden in view-only mode (display version above replaces them). */}
      {!viewOnly && selectedShift && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={LABEL_CLS}>
              Start time <span className="text-[var(--status-bad)]">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <select
                aria-label="Start hour"
                value={startParts.hour ?? ''}
                onChange={(e) => {
                  const h = e.target.value === '' ? null : Number(e.target.value);
                  // Default minute to 00 when picking the hour first.
                  const m = startParts.minute ?? (h !== null ? 0 : null);
                  updateStart(h, m);
                }}
                disabled={!timePickerEnabled}
                className={SELECT_CLS}
              >
                <option value="">HH</option>
                {startHours.map((h) => (
                  <option key={h} value={h}>
                    {hourLabel(h)}
                  </option>
                ))}
              </select>
              <select
                aria-label="Start minute"
                value={startParts.minute ?? ''}
                onChange={(e) => {
                  const m = e.target.value === '' ? null : Number(e.target.value);
                  updateStart(startParts.hour, m);
                }}
                disabled={!timePickerEnabled || startParts.hour === null}
                className={SELECT_CLS}
              >
                <option value="">MM</option>
                {MINUTE_OPTIONS.map((m) => (
                  <option key={m} value={m}>
                    {String(m).padStart(2, '0')} min
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className={LABEL_CLS}>
              End time <span className="text-[var(--status-bad)]">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <select
                aria-label="End hour"
                value={endParts.hour ?? ''}
                onChange={(e) => {
                  const h = e.target.value === '' ? null : Number(e.target.value);
                  const m = endParts.minute ?? (h !== null ? 0 : null);
                  updateEnd(h, m);
                }}
                disabled={!timePickerEnabled || startParts.hour === null}
                className={SELECT_CLS}
              >
                <option value="">HH</option>
                {filteredEndHours.map((h) => (
                  <option key={h} value={h}>
                    {hourLabel(h)}
                  </option>
                ))}
              </select>
              <select
                aria-label="End minute"
                value={endParts.minute ?? ''}
                onChange={(e) => {
                  const m = e.target.value === '' ? null : Number(e.target.value);
                  updateEnd(endParts.hour, m);
                }}
                disabled={!timePickerEnabled || endParts.hour === null}
                className={SELECT_CLS}
              >
                <option value="">MM</option>
                {filteredEndMinutes.map((m) => (
                  <option key={m} value={m}>
                    {String(m).padStart(2, '0')} min
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* When a shift is selected but its hour list is degenerate
          (e.g. start == end), show Flutter's "No Slots Available"
          message in the pickers' place — `break_widget.dart:518-526`. */}
      {!viewOnly && selectedShift && startHours.length === 0 && (
        <p className="[font:var(--type-body)] text-[var(--ink-meta)]">No Slots Available</p>
      )}
    </div>
  );
}
